import { useMutation } from '@tanstack/react-query';
import { useCountdown, useDebounceCallback } from 'usehooks-ts';
import { z } from 'zod';
import type { RespondedType, ResponseError } from '@/models/response';
import { useAuthStore } from '@/stores/auth';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import generateEncryptedToken from '@/utils/generate-encrypted-token';

export default function useRequestOtp(type?: 'sign-up' | 'sign-in') {
  const [countdown, { startCountdown, resetCountdown }] = useCountdown({
    countStart: 60,
    countStop: 0,
  });

  const mutation = useMutation<RespondedType, ResponseError, RequestOtpRequest>({
    mutationKey: ['/GETVABCP'],
    mutationFn: async (params) =>
      fetcher<RespondedType>(
        '/GETVABCP',
        {
          Phone: params.Phone,
          TelCode: params.TelCode,
          Token: await generateEncryptedToken(params.Phone),
          Type: type === 'sign-up' ? '1' : '2',
        },
        { requireAuth: false, validResponseCodes: [0] },
      ),
    onSuccess: () => {
      startCountdown();
    },
    onError: () => {
      resetCountdown();
      throttledMutate.cancel();
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
    onMutate: () => {
      resetCountdown();
    },
  });

  const throttledMutate = useDebounceCallback(mutation.mutateAsync, 30000, { leading: true, trailing: false });

  return {
    countdown,
    request: throttledMutate,
    ...mutation,
  };
}

export function useVerifyOtp() {
  const mutation = useMutation<VerifyOtpResponse, ResponseError, VerifyOtpRequest>({
    mutationKey: ['/CheckPhoneCode'],
    mutationFn: (params) =>
      fetcher<VerifyOtpResponse>('/CheckPhoneCode', params, {
        requireAuth: false,
        schema: verifyOtpResponseSchema,
        validResponseCodes: [0],
      }),
    onSuccess: (data) => {
      if (data.Code === 0 && data.UID) {
        useAuthStore.getState().start({
          UserID: data.UID,
          // NOTE: no need to get nickname and avatar url
          NickName: '',
          AvatarUrl: '',
          Token: data.Token || data.AccessToken || '',
          RefreshToken: data.RefreshToken || '',
        });
      }
    },
    onSettled: (data, error) =>
      addToast({
        type: error ? 'error' : 'success',
        message: error?.message ?? data?.Msg,
      }),
  });

  return {
    isVerified: mutation.data?.Code === 0,
    ...mutation,
  };
}

const requestOtpRequestSchema = z.object({
  Phone: z.string().regex(/^\d+$/),
  TelCode: z.string(),
});

const verifyOtpRequestSchema = requestOtpRequestSchema.extend({
  Code: z.string().min(6),
});

const verifyOtpResponseSchema = z.object({
  Code: z.number(),
  Msg: z.string(),
  UID: z.number(),
  Token: z.string().nullish(),
  AccessToken: z.string().nullish(),
  RefreshToken: z.string().nullish(),
});

type RequestOtpRequest = z.infer<typeof requestOtpRequestSchema>;

type VerifyOtpRequest = z.infer<typeof verifyOtpRequestSchema>;

type VerifyOtpResponse = z.infer<typeof verifyOtpResponseSchema>;
